import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import {
  DNRPreferencesInfoRec,
  dnrPreferencesService,
} from '../../../../services/dnrPreferencesInfo';
import { tasksService } from '../../../../services/tasks';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('DNRPreferencesData');

type RouteParams = {
  dnrPreferencesId?: number;
  targetUserId?: string;
  viewOnly?: boolean;
};

type DNRPreferencesDataScreenRouteProp = RouteProp<RootStackParamList, 'DNRPreferencesData'>;

type InfoItemProps = {
  label: string;
  value: string | boolean | undefined;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => {
  let displayValue = '';

  if (typeof value === 'boolean') {
    displayValue = value ? 'Yes' : 'No';
  } else if (typeof value === 'string') {
    displayValue = value || 'Not specified';
  } else {
    displayValue = 'Not specified';
  }

  return (
    <View style={styles.infoItem}>
      <Text style={styles.label}>{label}</Text>
      <Text style={styles.value}>{displayValue}</Text>
      <View style={styles.separator} />
    </View>
  );
};

export const DNRPreferencesDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<DNRPreferencesDataScreenRouteProp>();
  const [dnrPreferencesInfo, setDNRPreferencesInfo] = useState<DNRPreferencesInfoRec | null>(null);
  const [loading, setLoading] = useState(true);

  const dnrPreferencesId = route.params?.dnrPreferencesId;
  const targetUserId = route.params?.targetUserId;
  const viewOnly = route.params?.viewOnly || false;

  useEffect(() => {
    loadDNRPreferencesInfo();
  }, []);

  const loadDNRPreferencesInfo = async () => {
    try {
      if (dnrPreferencesId) {
        const info = await dnrPreferencesService.getDNRPreferencesById(dnrPreferencesId, targetUserId);
        setDNRPreferencesInfo(info);
      } else {
        // Load the first/latest entry if no specific ID provided
        const allInfo = await dnrPreferencesService.getAllDNRPreferences(targetUserId);
        if (allInfo.length > 0) {
          setDNRPreferencesInfo(allInfo[0]);
        }
      }
    } catch (error) {
      logger.error('Error loading DNR preferences info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (dnrPreferencesInfo) {
      // Map the data from dnrPreferencesInfo to the format expected by DNRPreferencesInputScreen
      const mappedData = {
        id: dnrPreferencesInfo.id,
        dnrType: dnrPreferencesInfo.dnrType,
        locationOfDirective: dnrPreferencesInfo.locationOfDirective,
        specialInstructions: dnrPreferencesInfo.specialInstructions,
        dnrDetails: dnrPreferencesInfo.dnrDetails,
        notes: dnrPreferencesInfo.notes,
      };

      navigation.navigate('DNRPreferencesInput', {
        dnrPreferencesData: mappedData,
        isEditing: true
      });
    } else {
      navigation.navigate('DNRPreferencesInput');
    }
  };

  const handleDelete = () => {
    if (dnrPreferencesInfo) {
      Alert.alert(
        'Delete DNR Preferences',
        'Are you sure you want to delete these DNR preferences? This action cannot be undone.',
        [
          {
            text: 'Cancel',
            style: 'cancel',
          },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              try {
                await dnrPreferencesService.deleteDNRPreferences(dnrPreferencesInfo.id!);

                // CRITICAL: Check if this was the last entry and reset task if so
                const remainingData = await dnrPreferencesService.getAllDNRPreferences();
                if (!remainingData || remainingData.length === 0) {
                  await tasksService.resetTask('dnr_preferences');
                }

                (navigation as any).navigate('HealthcareDirectives', { screen: 'HealthcareDirectivesChecklist', params: { refreshKey: Date.now() } });
              } catch (error) {
                logger.error('Error deleting DNR preferences info:', error);
                Alert.alert('Error', 'Failed to delete DNR preferences');
              }
            },
          },
        ]
      );
    }
  };

  const handleBack = () => {
    (navigation as any).navigate('HealthcareDirectives', { screen: 'HealthcareDirectivesChecklist', params: { refreshKey: Date.now() } });
  };

  const formatDNRType = (dnrType?: string): string => {
    if (!dnrType) return 'Not specified';

    const typeMapping: Record<string, string> = {
      'full_dnr': 'Full DNR (No Resuscitation)',
      'limited': 'Limited Resuscitation',
      'specific': 'Specific Medical Situation',
      'natural': 'Natural Death'
    };

    return typeMapping[dnrType] || dnrType;
  };

  const formatDNRDetails = (dnrDetails: any, dnrType?: string): string => {
    if (!dnrDetails || typeof dnrDetails !== 'object') {
      return 'Not specified';
    }

    const details: string[] = [];

    if (dnrType === 'full_dnr') {
      if (dnrDetails.specificInterventionsToDecline) {
        details.push(`Interventions to Decline: ${dnrDetails.specificInterventionsToDecline}`);
      }
      if (dnrDetails.discussedWithHealthcareProvider !== undefined) {
        details.push(`Discussed with Healthcare Provider: ${dnrDetails.discussedWithHealthcareProvider ? 'Yes' : 'No'}`);
      }
      if (dnrDetails.conditionsForCPR) {
        details.push(`Conditions for CPR: ${dnrDetails.conditionsForCPR}`);
      }
      if (dnrDetails.additionalInstructions) {
        details.push(`Additional Instructions: ${dnrDetails.additionalInstructions}`);
      }
    } else if (dnrType === 'limited') {
      if (dnrDetails.acceptableInterventions) {
        details.push(`Acceptable Interventions: ${dnrDetails.acceptableInterventions}`);
      }
      if (dnrDetails.limitedResuscitationConditions) {
        details.push(`Limited Resuscitation Conditions: ${dnrDetails.limitedResuscitationConditions}`);
      }
      if (dnrDetails.medicationPreferences) {
        details.push(`Medication Preferences: ${dnrDetails.medicationPreferences}`);
      }
      if (dnrDetails.timeLimits) {
        details.push(`Time Limits: ${dnrDetails.timeLimits}`);
      }
    } else if (dnrType === 'specific') {
      if (dnrDetails.triggeringConditions) {
        details.push(`Triggering Conditions: ${dnrDetails.triggeringConditions}`);
      }
      if (dnrDetails.exceptions) {
        details.push(`Exceptions: ${dnrDetails.exceptions}`);
      }
      if (dnrDetails.treatmentPreferences) {
        details.push(`Treatment Preferences: ${dnrDetails.treatmentPreferences}`);
      }
      if (dnrDetails.discussedWithDoctor !== undefined) {
        details.push(`Discussed with Doctor: ${dnrDetails.discussedWithDoctor ? 'Yes' : 'No'}`);
      }
    } else if (dnrType === 'natural') {
      if (dnrDetails.comfortCarePreferences) {
        details.push(`Comfort Care Preferences: ${dnrDetails.comfortCarePreferences}`);
      }
      if (dnrDetails.painManagementOptions) {
        details.push(`Pain Management Options: ${dnrDetails.painManagementOptions}`);
      }
      if (dnrDetails.spiritualCulturalPractices) {
        details.push(`Spiritual/Cultural Practices: ${dnrDetails.spiritualCulturalPractices}`);
      }
      if (dnrDetails.environmentPreferences) {
        details.push(`Environment Preferences: ${dnrDetails.environmentPreferences}`);
      }
    }

    return details.length > 0 ? details.join('\n\n') : 'Not specified';
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>
        <Text style={styles.title}>DNR Preferences Details</Text>
        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <Text style={styles.loadingText}>Loading...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>DNR Preferences Details</Text>
      <View style={styles.whiteContainer}>
        <ScrollView style={styles.scrollView}>
          {viewOnly && (
            <View style={styles.viewOnlyBanner}>
              <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
            </View>
          )}
          {dnrPreferencesInfo ? (
            <>
              <InfoItem label="DNR Type" value={formatDNRType(dnrPreferencesInfo.dnrType)} />

              {dnrPreferencesInfo.locationOfDirective && (
                <InfoItem label="Location of Directive" value={dnrPreferencesInfo.locationOfDirective} />
              )}

              {dnrPreferencesInfo.specialInstructions && (
                <InfoItem label="Special Instructions" value={dnrPreferencesInfo.specialInstructions} />
              )}

              {dnrPreferencesInfo.dnrDetails && (
                <>
                  <Text style={styles.sectionTitle}>DNR Details</Text>
                  <InfoItem label="Details" value={formatDNRDetails(dnrPreferencesInfo.dnrDetails, dnrPreferencesInfo.dnrType)} />
                </>
              )}

              {dnrPreferencesInfo.notes && (
                <InfoItem label="Additional Notes" value={dnrPreferencesInfo.notes} />
              )}

              <View style={styles.buttonContainer}>
                <TouchableOpacity
                  style={[styles.button, styles.backToListButton]}
                  onPress={handleBack}
                >
                  <Text style={styles.buttonText}>Back</Text>
                </TouchableOpacity>

                {!viewOnly && (
                  <>
                    <TouchableOpacity
                      style={[styles.button, styles.deleteButton]}
                      onPress={handleDelete}
                    >
                      <Text style={styles.buttonText}>Delete</Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={[styles.button, styles.editButton]}
                      onPress={handleEdit}
                    >
                      <Text style={styles.buttonText}>Edit</Text>
                    </TouchableOpacity>
                  </>
                )}
              </View>
            </>
          ) : (
            <View style={styles.noDataContainer}>
              <Text style={styles.noDataText}>No DNR preferences found.</Text>
              <TouchableOpacity
                style={[styles.button, styles.editButton, { marginTop: 20 }]}
                onPress={handleEdit}
              >
                <Text style={styles.buttonText}>Add DNR Preferences</Text>
              </TouchableOpacity>
            </View>
          )}
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0', // Teal header background
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF', // White title on teal background
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF', // White content area
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  infoItem: {
    marginBottom: ms(20),
  },
  label: {
    fontSize: ms(16, 0.3),
    color: '#6B7280', // Gray label text
    marginBottom: ms(8),
  },
  value: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F', // Dark blue value text
    marginBottom: ms(8),
  },
  separator: {
    height: s(2),
    backgroundColor: '#29BFC0', // Teal separator line
    marginTop: ms(8),
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: ms(20),
    paddingBottom: ms(130),
    gap: ms(10),
  },
  button: {
    flex: 1,
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0', // Teal back button
  },
  deleteButton: {
    backgroundColor: '#FF4444', // Red delete button
  },
  editButton: {
    backgroundColor: '#FF9F5A', // Orange edit button
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  loadingText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
  },
  noDataContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  noDataText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
  },
  sectionTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginTop: ms(20),
    marginBottom: ms(15),
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});